import { BilledProduct, BillingAdjustmentFormula, KeyValueId, ProductTaxes, KeyValueIdGroup, ReceiptOption } from "src/service/local.service";
import { getDistinctAsString } from "./General";

export const IsSpecificTaxAvailableForProduct = (prdTaxes: BilledProduct[], selectedReceiptOptions: ReceiptOption[]): boolean => {
    let rtn = false;

    if (selectedReceiptOptions.includes(ReceiptOption.ProductTaxesRow)) {
        rtn = true;
    }

    if (prdTaxes) {
        prdTaxes.forEach(prdTax => {
            if (!rtn && prdTax.taxes) {
                rtn = prdTax.taxes.filter(x => x.value && parseFloat(x.value.toString()) >= 0).length > 0;
            }
        });
    }

    if (!rtn) {
        const businessTaxCategoryIds = prdTaxes.map(x => x.details.v24);

        if (businessTaxCategoryIds.length > 0 && getDistinctAsString(businessTaxCategoryIds).length > 1) {
            rtn = true;
        }
    }
    return rtn;
}

export const GetBaseAmountAll = (products: BilledProduct[], isTaxFree: boolean): number => {
    let rtn = 0;
    products = isTaxFree ? products.filter(x => x.details.v26 == '1') : products.filter(x => !x.details.v26 || x.details.v26 == '0');
    rtn = products.map(x => parseFloat(x.details.v4) * parseFloat(x.details.v1)).reduce((sum, current) => sum + current, 0);
    return rtn;
}

export const GetTaxableAmountAll = (products: BilledProduct[], isNotConsiderTax: boolean, businessTaxesWithCategory: KeyValueIdGroup[], isTaxFree: boolean): number => {
    let rtn = 0;
    const productTaxes = ConvertToProdTaxes(products ?? []);
    products = isTaxFree ? products.filter(x => x.details.v26 == '1') : products.filter(x => !x.details.v26 || x.details.v26 == '0');
    products.forEach((item, idx) => {
        const productId = products[idx]?.productId ?? '';
        const batchId = products[idx]?.details.v15 ?? '';
        const measurementType = products[idx]?.details.v5 ?? '';
        rtn += GetTaxableAmountForProduct(products, productId, batchId, measurementType, productTaxes, isNotConsiderTax, businessTaxesWithCategory);
    });

    return rtn;
}

export const GetTaxAmountAll = (products: BilledProduct[], isNotConsiderTax: boolean, businessTaxesWithCategory: KeyValueIdGroup[], key?: string, baseAmountSrc?: number, isDiscount?: boolean): number => {
    let rtn = 0;
    const baseAmount = baseAmountSrc ? baseAmountSrc : GetBaseAmountAll(products, false);
    if (!isNotConsiderTax && products.length > 0) {
        const taxCategoryId = products[0]?.details.v24;
        businessTaxesWithCategory.filter(x => x.key == taxCategoryId).forEach(businessTaxes => {
            if (baseAmount > 0) {
                let totalTemp = 0;
                if (key) {
                    const tax = businessTaxes.values.filter(tax => isDiscount ? tax.key == 'discount' : tax.key != 'discount').find(x => x.key == key);
                    if (tax) {
                        if (tax.value) {
                            const dblPercentage: number = (parseFloat(tax.value.toString()) / 100) * baseAmount;
                            totalTemp += dblPercentage;
                        }
                    }
                    rtn += totalTemp;
                } else {
                    businessTaxes.values.filter(tax => isDiscount ? tax.key == 'discount' : tax.key != 'discount').forEach(tax => {
                        if (tax) {
                            if (tax.value) {
                                totalTemp = 0;
                                const dblPercentage: number = (parseFloat(tax.value.toString()) / 100) * baseAmount;
                                totalTemp += dblPercentage;
                                rtn += totalTemp;
                            }
                        }
                    });
                }
            }
        });
    } else {
        rtn = baseAmount;
    }
    return rtn;
}

export const GetBaseAmountForProduct = (products: BilledProduct[], productId: string, batchId: string, measurementTypeStr: string): number => {
    let rtn = 0;
    rtn = products.filter(x => x.productId == productId && (!x.details.v15 || x.details.v15 == batchId) && (!x.details.v5 || x.details.v5 == measurementTypeStr)).map(x => parseFloat(x.details.v4) * parseFloat(x.details.v1)).reduce((sum, current) => sum + current, 0);
    return rtn;
}

export const GetTaxableAmountForProduct = (products: BilledProduct[], productId: string, batchId: string, measurementTypeStr: string, prdTaxes: ProductTaxes[], isNotConsiderTax: boolean, businessTaxes: KeyValueIdGroup[], isExcludeTaxFree?: boolean): number => {
    let rtn = 0;
    let baseAmount = GetBaseAmountForProduct(products, productId, batchId, measurementTypeStr);

    const isTaxFree: boolean = products.find(x => x.productId == productId)?.details.v26 == '1' ? true : false;
    if (isTaxFree) {
        return isExcludeTaxFree ? 0 : baseAmount;
    }

    const businessTaxCategoryId = products.find(x => x.productId == productId)?.details.v24 ?? '';
    const requiredDiscount = GetRequiredTaxForProduct(prdTaxes, productId, businessTaxCategoryId, measurementTypeStr, businessTaxes, batchId, true);
    const discountPercentages = requiredDiscount.filter(x => x.value && x.value.toString().length > 0).map(x => parseFloat(x.value?.toString() ?? '0'));
    const discountPercentage = discountPercentages.reduce((sum, current) => sum + current, 0);
    const discountAmount = (discountPercentage / 100) * baseAmount;
    baseAmount = baseAmount - discountAmount;

    if (isNotConsiderTax) {
        const requiredTaxes = GetRequiredTaxForProduct(prdTaxes, productId, businessTaxCategoryId, measurementTypeStr, businessTaxes, batchId);
        const totalTaxPercentages = requiredTaxes.filter(x => x.value && x.value.toString().length > 0).map(x => parseFloat(x.value?.toString() ?? '0'));
        const totalTaxPercentage = totalTaxPercentages.reduce((sum, current) => sum + current, 0);
        const taxableAmount = (baseAmount / (1 + (totalTaxPercentage / 100)));
        rtn = taxableAmount;
    } else {
        rtn = baseAmount;
    }
    return rtn;
}

export const GetRequiredTaxForProduct = (prdTaxes: ProductTaxes[], productId: string, businessTaxCategoryId: string, measurementTypeStr: string, businessTaxes: KeyValueIdGroup[], batchId?: string, isDiscount?: boolean): KeyValueId[] => {
    let rtn: KeyValueId[] = [];

    prdTaxes.filter(x => !x.batchId).forEach(prdTax => {
        prdTax.batchId = '';
    });
    if (!batchId) {
        batchId = '';
    }

    const taxProduct = prdTaxes ? prdTaxes.find(x => x.productId == productId && (!x.batchId || x.batchId == batchId) && (!x.measurementTypeStr || x.measurementTypeStr == measurementTypeStr)) : undefined;

    if (taxProduct && taxProduct.taxes && taxProduct.taxes.filter(x => x.value).length > 0) {
        rtn = taxProduct.taxes.filter(x => x.value && parseFloat(x.value.toString()) >= 0);
        if (businessTaxes) {
            businessTaxes.find(x => x.key == businessTaxCategoryId)?.values.forEach(businessTax => {
                const item = rtn.find(x => x.key == businessTax.key);
                if (!item && businessTax.value && parseFloat(businessTax.value.toString()) > 0) {
                    rtn.push(businessTax);
                }
            });
        }
    } else if (businessTaxes) {
        rtn = businessTaxes.find(x => x.key == businessTaxCategoryId)?.values.filter(x => x.value && parseFloat(x.value.toString()) > 0) ?? [];
    }
    rtn = rtn.filter(x => x.value != '0');
    return rtn.filter(x => isDiscount ? x.key == 'discount' : x.key != 'discount');
}

export const GetAdjustmentAmount = (totalAmount: number, discountAmount?: number, adjustmentFormulas?: BillingAdjustmentFormula[], adjustmentAmount?: number): number => {
    if (!adjustmentAmount) {
        adjustmentAmount = 0;
    }
    if (!discountAmount) {
        discountAmount = 0;
    }
    totalAmount = totalAmount - discountAmount;
    if (adjustmentFormulas) {
        const totalAmountStr = totalAmount.toFixed(2);
        if (totalAmountStr.indexOf('.') > -1 && !totalAmountStr.endsWith('.00')) {
            const totalDecimal = parseFloat('0.' + totalAmountStr.split('.')[1]);
            const adjustmentFormula = adjustmentFormulas.find(x => x.from <= totalDecimal && x.to >= totalDecimal);
            if (adjustmentFormula) {
                const totalAmountNow: number = parseFloat(totalAmountStr.split('.')[0] ?? '0') + adjustmentFormula.value;
                adjustmentAmount = totalAmountNow - totalAmount;
            }
        }
    }

    return parseFloat(adjustmentAmount.toFixed(2));
}

export const GetTaxPercentageForProduct = (products: BilledProduct[], prdTaxes: ProductTaxes[], isNotConsiderTax: boolean, businessTaxes: KeyValueIdGroup[], productId: string, batchId: string, measurementTypeStr: string, taxNameKey?: string, baseAmountSrc?: number, isDiscount?: boolean): number => {
    let rtn = 0;
    const baseAmount = baseAmountSrc ? baseAmountSrc : GetBaseAmountForProduct(products, productId, batchId, measurementTypeStr);
    if (!isNotConsiderTax) {
        if (baseAmount > 0) {
            let totalTemp = 0;
            const businessTaxCategoryId = products.find(x => x.productId == productId)?.details.v24 ?? '';
            const taxes = GetRequiredTaxForProduct(prdTaxes, productId, businessTaxCategoryId, measurementTypeStr, businessTaxes, batchId, isDiscount);
            if (taxNameKey) {
                const tax = taxes.filter(tax => isDiscount ? tax.key == 'discount' : tax.key != 'discount').find(x => x.key == taxNameKey);
                if (tax) {
                    if (tax.value) {
                        const dblPercentage: number = parseFloat(tax.value.toString());
                        totalTemp += dblPercentage;
                    }
                }
                rtn += totalTemp;
            } else {
                (taxes ?? businessTaxes.find(x => x.key == businessTaxCategoryId)?.values).forEach(businessTax => {
                    const tax = taxes.filter(tax => isDiscount ? tax.key == 'discount' : tax.key != 'discount').find(x => x.key == businessTax.key);
                    if (tax) {
                        if (tax.value) {
                            const dblPercentage: number = parseFloat(tax.value.toString());
                            totalTemp += dblPercentage;
                        }
                    }
                });
                rtn += totalTemp;
            }
        }
    } else {
        rtn = baseAmount;
    }
    return rtn;
}

export const GetTaxAmountForProduct = (products: BilledProduct[], prdTaxes: ProductTaxes[], isNotConsiderTax: boolean, businessTaxes: KeyValueIdGroup[], productId: string, batchId: string, measurementTypeStr: string, taxNameKey?: string, taxableAmount?: number, isDiscount?: boolean): number => {
    let rtn = 0;
    const baseAmount = taxableAmount ? taxableAmount : GetBaseAmountForProduct(products, productId, batchId, measurementTypeStr);
    if (!isNotConsiderTax) {
        if (baseAmount > 0) {
            let totalTemp = 0;
            const businessTaxCategoryId = products.find(x => x.productId == productId)?.details.v24 ?? '';
            const taxes = GetRequiredTaxForProduct(prdTaxes, productId, businessTaxCategoryId, measurementTypeStr, businessTaxes, batchId, isDiscount);
            if (taxNameKey) {
                const tax = taxes.filter(tax => isDiscount ? tax.key == 'discount' : tax.key != 'discount').find(x => x.key == taxNameKey);
                if (tax) {
                    if (tax.value) {
                        const dblPercentage: number = (parseFloat(tax.value.toString()) / 100) * baseAmount;
                        totalTemp += dblPercentage;
                    }
                }
                rtn += totalTemp;
            } else {
                (taxes ?? businessTaxes.find(x => x.key == businessTaxCategoryId)?.values).forEach(businessTax => {
                    const tax = taxes.filter(tax => isDiscount ? tax.key == 'discount' : tax.key != 'discount').find(x => x.key == businessTax.key);
                    if (tax) {
                        if (tax.value) {
                            const dblPercentage: number = (parseFloat(tax.value.toString()) / 100) * baseAmount;
                            totalTemp += dblPercentage;
                        }
                    }
                });
                rtn += totalTemp;
            }
        }
    } else {
        rtn = baseAmount;
    }
    return rtn;
}

export const GetTaxAmountIncludeTaxForProductId = (products: BilledProduct[], prdTaxes: ProductTaxes[], isNotConsiderTax: boolean, businessTaxes: KeyValueIdGroup[], productId: string, batchId: string, measurementTypeStr: string, businessTaxCategoryId?: string, isDiscount?: boolean): number => {
    let rtn = 0;
    const baseAmount = GetTaxableAmountForProduct(products, productId, batchId, measurementTypeStr, prdTaxes, isNotConsiderTax, businessTaxes);
    const isTaxFree: boolean = products.find(x => x.productId == productId)?.details.v26 == '1' ? true : false;

    if (isTaxFree) {
        return baseAmount;
    }

    if (!isNotConsiderTax) {
        if (baseAmount > 0) {
            if (!businessTaxCategoryId) {
                businessTaxCategoryId = products.find(x => x.productId == productId)?.details.v24 ?? '';
            }
            const taxes = GetRequiredTaxForProduct(prdTaxes, productId, businessTaxCategoryId, measurementTypeStr, businessTaxes, batchId, isDiscount);
            taxes.forEach(tax => {
                rtn += GetTaxAmountForProduct(products, prdTaxes, isNotConsiderTax, businessTaxes, productId, batchId, measurementTypeStr, tax.key, baseAmount, isDiscount);
            });
            rtn += baseAmount;
        }
    } else {
        const businessTaxCategoryId = products.find(x => x.productId == productId)?.details.v24 ?? '';
        const requiredTaxes = GetRequiredTaxForProduct(ConvertToProdTaxes(products ?? []), productId, businessTaxCategoryId, measurementTypeStr ?? '', businessTaxes ?? [], batchId, isDiscount);
        let taxAmountFinal = 0;
        requiredTaxes.filter(x => (isDiscount ? x.key == 'discount' : x.key != 'discount') && x.value && x.value.toString().length > 0).forEach(requiredTax => {
            if (requiredTax && requiredTax.value) {
                const percentage = parseFloat(requiredTax.value.toString().trim());
                taxAmountFinal += (baseAmount * percentage) / 100;
            }
        });
        rtn += (baseAmount + taxAmountFinal);
    }
    return rtn;
}

export const GetServiceChargeAmount = (serviceChargePercentage?: number, totalBaseAmount?: number, products?: BilledProduct[]): number => {
    let total = 0;

    if (serviceChargePercentage && serviceChargePercentage > 0) {
        if (!totalBaseAmount) {
            totalBaseAmount = GetBaseAmountAll(products ?? [], false);
        }
        if (totalBaseAmount && totalBaseAmount > 0) {
            total = (totalBaseAmount / 100) * serviceChargePercentage;
        }
    }

    return total;
}

export const GetPriceIncludeTax = (products: BilledProduct[], isNotConsiderTax: boolean, businessTaxes: KeyValueIdGroup[], isDiscount?: boolean): number => {
    let total = 0;
    if (products && products && products.length > 0) {
        const prdTaxes: ProductTaxes[] = ConvertToProdTaxes(products);

        products.forEach(product => {
            total += GetTaxAmountIncludeTaxForProductId(products, prdTaxes, isNotConsiderTax, businessTaxes, product.productId, product.details.v15, product.details.v5, product.details.v24, isDiscount);
        });
    }
    return total;
}

export const ConvertToProdTaxes = (products: BilledProduct[]): ProductTaxes[] => {
    let prdTaxes: ProductTaxes[] = [];
    products.forEach(product => {
        prdTaxes.push({
            productId: product.productId,
            batchId: product.details.v15,
            taxes: product.taxes,
            measurementTypeStr: product.details.v5
        } as ProductTaxes);
    });
    return prdTaxes;
}